import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import UnlockClientModal from './UnlockClientModal';
import { isClientUnlocked, unlockClientWithPassphrase, getClientSessionKey } from '../crypto/sessionKeyStore';
import { decryptFromEnvelope } from '../crypto/envelope';

const CATEGORIES = [
  { num: 1, name: 'Financial Affidavit', folder: '01_Financial_Affidavit' },
  { num: 2, name: 'Tax Returns', folder: '02_Tax_Returns' },
  { num: 3, name: 'IRS Forms/Transcripts', folder: '03_IRS_Forms' },
  { num: 4, name: 'Paystubs', folder: '04_Paystubs' },
  { num: 5, name: 'Income', folder: '05_Income' },
  { num: 6, name: 'Loan Applications', folder: '06_Loan_Applications' },
  { num: 7, name: 'Real Estate', folder: '07_Real_Estate' },
  { num: 8, name: 'Bank Accounts', folder: '08_Bank_Accounts' },
  { num: 9, name: 'Brokerage Accounts', folder: '09_Brokerage_Accounts' },
  { num: 10, name: 'Retirement Accounts', folder: '10_Retirement_Accounts' },
  { num: 11, name: 'Virtual Currency', folder: '11_Virtual_Currency' },
  { num: 12, name: 'Life Insurance', folder: '12_Life_Insurance' },
  { num: 13, name: 'Health Insurance', folder: '13_Health_Insurance' },
  { num: 14, name: 'Corporate Tax Returns', folder: '14_Corporate_Tax' },
  { num: 15, name: 'Indebtedness', folder: '15_Indebtedness' },
  { num: 16, name: 'Agreements', folder: '16_Agreements' },
  { num: 17, name: 'Modification Agreements', folder: '17_Modification' },
  { num: 18, name: 'Unequal Distribution', folder: '18_Unequal_Distribution' },
  { num: 19, name: 'Court Orders', folder: '19_Court_Orders' },
];

function FileExplorer() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [pendingDownloadKey, setPendingDownloadKey] = useState('');

  useEffect(() => {
    if (clientId) {
      loadClient();
    }
  }, [clientId]);

  useEffect(() => {
    if (selectedCategory) {
      const prefix = buildPrefix(selectedCategory);
      if (prefix) {
        loadFiles(prefix);
      }
    }
  }, [selectedCategory, clientId]);

  const loadClient = async () => {
    try {
      const data = await apiClient.getClient(clientId);
      setClient(data);
      setError(null);
    } catch (err) {
      setError('Failed to load client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const buildPrefix = (category) => {
    if (!category || !clientId) {
      return '';
    }

    if (category.scope === 'unsorted') {
      return `${clientId}/_UNSORTED/`;
    }
    if (category.scope === 'input') {
      return `${clientId}/_INPUT/`;
    }
    return `${clientId}/_OUTPUT/${category.folder}/`;
  };

  const loadFiles = async (prefix) => {
    try {
      setLoading(true);
      const data = await apiClient.listFiles(prefix);
      setFiles(data.files || []);
    } catch (err) {
      console.error('Failed to load files:', err);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file) => {
    const key = file?.key;
    if (!client) {
      return;
    }
    if (!isClientUnlocked(client.client_id)) {
      setPendingDownloadKey(key);
      setUnlockError('');
      setShowUnlockModal(true);
      return;
    }

    return downloadFileNow(file);
  };

  const downloadFileNow = async (file) => {
    const key = file?.key;
    try {
      const url = await apiClient.getDownloadUrl(key);
      const encryptedResponse = await fetch(url);
      if (!encryptedResponse.ok) {
        throw new Error('Encrypted download failed.');
      }

      const payload = await encryptedResponse.json();
      const envelope = payload?.envelope;
      if (!envelope) {
        throw new Error('Encrypted payload envelope is missing.');
      }

      const dekBytes = getClientSessionKey(client.client_id);
      if (!dekBytes) {
        throw new Error('Client is locked.');
      }

      const plaintextBytes = await decryptFromEnvelope({
        keyBytes: dekBytes,
        envelope,
      });

      const filename = payload?.encryption?.plaintext_filename
        || (key ? key.split('/').pop() : 'document.pdf');
      const mimeType = payload?.encryption?.plaintext_content_type || 'application/pdf';
      const blob = new Blob([plaintextBytes], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError('Failed to decrypt and download file. Verify passphrase and retry.');
      console.error('Failed to decrypt and download file:', err);
    }
  };

  const handleUnlock = async (passphrase) => {
    if (!client) {
      return;
    }

    try {
      setUnlocking(true);
      setUnlockError('');
      await unlockClientWithPassphrase(client, passphrase);
      setShowUnlockModal(false);

      if (pendingDownloadKey) {
        const targetKey = pendingDownloadKey;
        setPendingDownloadKey('');
        await downloadFileNow({ key: targetKey });
      }
    } catch (err) {
      setUnlockError(err.message || 'Unlock failed.');
    } finally {
      setUnlocking(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading && !client) {
    return <div className="card text-center">Loading...</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="badge badge-error">{error}</div>
        <Link to="/" className="btn btn-primary mt-4">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">{client?.client_name}</h2>
            {client?.case_number && (
              <div className="text-sm text-muted">{client.case_number}</div>
            )}
          </div>
          <Link to="/" className="btn btn-secondary">
            Back to Clients
          </Link>
        </div>

        <div className="grid grid-2 gap-4">
          {/* Category List */}
          <div>
            <h3 className="card-title mb-4">Categories</h3>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.num}
                  className={`file-item ${
                    selectedCategory?.num === cat.num ? 'bg-primary-light' : ''
                  }`}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedCategory?.num === cat.num ? '#e0e7ff' : undefined,
                  }}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <div className="file-icon">📁</div>
                  <div className="file-info">
                    <div className="file-name">
                      {cat.num}. {cat.name}
                    </div>
                  </div>
                </div>
              ))}

              {/* Special folders */}
              <div
                className="file-item"
                style={{ cursor: 'pointer', marginTop: '1rem' }}
                onClick={() => setSelectedCategory({ num: 0, name: 'Unsorted', scope: 'unsorted' })}
              >
                <div className="file-icon">❓</div>
                <div className="file-info">
                  <div className="file-name">Unsorted / Low Confidence</div>
                </div>
              </div>

              <div
                className="file-item"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedCategory({ num: -1, name: 'Input', scope: 'input' })}
              >
                <div className="file-icon">📥</div>
                <div className="file-info">
                  <div className="file-name">Original Input Files</div>
                </div>
              </div>
            </div>
          </div>

          {/* File List */}
          <div>
            <h3 className="card-title mb-4">
              {selectedCategory ? selectedCategory.name : 'Select a category'}
            </h3>

            {loading ? (
              <p className="text-muted">Loading files...</p>
            ) : !selectedCategory ? (
              <p className="text-muted">Select a category to view files</p>
            ) : files.length === 0 ? (
              <p className="text-muted">No files in this category</p>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {files.map((file) => (
                  <div key={file.key} className="file-item">
                    <div className="file-icon">📄</div>
                    <div className="file-info">
                      <div className="file-name">
                        {file.key.split('/').pop()}
                      </div>
                      <div className="file-meta">
                        {formatSize(file.size)} · {formatDate(file.last_modified)}
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary"
                      onClick={() => downloadFile(file)}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Jobs */}
      {client?.recent_jobs?.length > 0 && (
        <div className="card">
          <h3 className="card-title mb-4">Recent Processing Jobs</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Files</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {client.recent_jobs.map((job) => (
                <tr key={job.job_id}>
                  <td className="text-sm">{job.job_id.slice(0, 8)}...</td>
                  <td>
                    {job.processed_files} / {job.total_files}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        job.status === 'completed'
                          ? 'badge-success'
                          : job.status === 'processing'
                          ? 'badge-warning'
                          : 'badge-pending'
                      }`}
                    >
                      {job.status}
                    </span>
                  </td>
                  <td className="text-sm">{formatDate(job.created_at)}</td>
                  <td>
                    <Link
                      to={`/status/${job.job_id}`}
                      className="btn btn-secondary"
                    >
                      Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UnlockClientModal
        open={showUnlockModal}
        clientName={client?.client_name}
        loading={unlocking}
        error={unlockError}
        onCancel={() => {
          setShowUnlockModal(false);
          setPendingDownloadKey('');
        }}
        onUnlock={handleUnlock}
      />
    </div>
  );
}

export default FileExplorer;
